import { ref, watch } from "vue";
import { uiSettingsService } from "../../../services/uiSettingsService";
import { TimePeriod, CompletionStatus } from "../../../types/enums";
import {
  getStartOfCurrentWeek,
  getEndOfCurrentWeek,
  getStartOfCurrentMonth,
  getEndOfCurrentMonth,
} from "../../../utils/dateUtils";

export function useTimeTrackerFilters() {
  // Load filter settings from localStorage
  const savedFilters = uiSettingsService.getFilterSettings();
  const selectedTopics = ref<string[]>(savedFilters.selectedTopics || []);
  const selectedProjects = ref<string[]>(savedFilters.selectedProjects || []);
  const selectedPeriod = ref(savedFilters.filterPeriod || TimePeriod.ALL);
  const dateRange = ref(savedFilters.dateRange || { start: "", end: "" });
  const selectedCompletionStatus = ref(
    savedFilters.selectedCompletionStatus || CompletionStatus.ALL
  );
  const filterBySessions = ref(
    savedFilters.filterBySessions !== undefined
      ? savedFilters.filterBySessions
      : true
  );

  console.log("Initial filter settings:", {
    selectedTopics: selectedTopics.value,
    selectedPeriod: selectedPeriod.value,
    dateRange: dateRange.value,
    selectedCompletionStatus: selectedCompletionStatus.value,
    filterBySessions: filterBySessions.value,
  });

  // Watch for changes and persist them
  watch(
    () => ({
      selectedPeriod: selectedPeriod.value,
      dateRange: dateRange.value,
      selectedTopics: selectedTopics.value,
      selectedProjects: selectedProjects.value,
      selectedCompletionStatus: selectedCompletionStatus.value,
      filterBySessions: filterBySessions.value,
    }),
    (newSettings) => {
      uiSettingsService.setFilterSettings({
        filterPeriod: newSettings.selectedPeriod,
        dateRange: newSettings.dateRange,
        selectedTopics: newSettings.selectedTopics,
        selectedProjects: newSettings.selectedProjects,
        selectedCompletionStatus: newSettings.selectedCompletionStatus,
        filterBySessions: newSettings.filterBySessions,
      });
    },
    { deep: true }
  );

  function isTaskInPeriod(task: any): boolean {
    console.log(
      "isTaskInPeriod called for task:",
      task.name,
      "createdAt:",
      task.createdAt,
      "filterBySessions:",
      filterBySessions.value
    );

    if (filterBySessions.value && task.sessions && task.sessions.length > 0) {
      // Filter by any session - check if ANY session falls within the period
      const today = new Date();
      const todayOnly = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );

      // Check if any session falls within the selected period
      return task.sessions.some((session: any) => {
        const sessionDate = new Date(session.startTime);
        const sessionDateOnly = new Date(
          sessionDate.getFullYear(),
          sessionDate.getMonth(),
          sessionDate.getDate()
        );

        switch (selectedPeriod.value) {
          case TimePeriod.ALL:
            return true;
          case TimePeriod.TODAY:
            return sessionDateOnly.getTime() === todayOnly.getTime();
          case TimePeriod.WEEK: {
            const startOfWeek = getStartOfCurrentWeek();
            const endOfWeek = getEndOfCurrentWeek();
            return (
              sessionDateOnly >= startOfWeek && sessionDateOnly <= endOfWeek
            );
          }
          case TimePeriod.MONTH: {
            const startOfMonth = getStartOfCurrentMonth();
            const endOfMonth = getEndOfCurrentMonth();
            return (
              sessionDateOnly >= startOfMonth && sessionDateOnly <= endOfMonth
            );
          }
          case TimePeriod.YEAR: {
            const firstDayOfYear = new Date(todayOnly.getFullYear(), 0, 1);
            return (
              sessionDateOnly >= firstDayOfYear && sessionDateOnly <= todayOnly
            );
          }
          case TimePeriod.CUSTOM: {
            if (!dateRange.value.start || !dateRange.value.end) {
              return true;
            } else {
              const startDate = new Date(dateRange.value.start);
              const endDate = new Date(dateRange.value.end);
              endDate.setHours(23, 59, 59, 999);
              startDate.setHours(0, 0, 0, 0);
              return sessionDateOnly >= startDate && sessionDateOnly <= endDate;
            }
          }
          default:
            return true;
        }
      });
    } else {
      // Filter by creation date or first session start time
      let taskDate: Date;
      if (task.sessions && task.sessions.length > 0) {
        taskDate = new Date(task.sessions[0].startTime);
      } else {
        taskDate = new Date(task.createdAt || new Date().toISOString());
      }

      console.log("Task date determined as:", taskDate);

      // Create date objects for comparison (year, month, day only)
      const taskDateOnly = new Date(
        taskDate.getFullYear(),
        taskDate.getMonth(),
        taskDate.getDate()
      );
      const today = new Date();
      const todayOnly = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );

      console.log("Task date only:", taskDateOnly, "Today only:", todayOnly);

      let result = false;
      switch (selectedPeriod.value) {
        case TimePeriod.ALL:
          result = true;
          break;
        case TimePeriod.TODAY:
          result = taskDateOnly.getTime() === todayOnly.getTime();
          break;
        case TimePeriod.WEEK: {
          const startOfWeek = getStartOfCurrentWeek();
          const endOfWeek = getEndOfCurrentWeek();
          result = taskDateOnly >= startOfWeek && taskDateOnly <= endOfWeek;
          break;
        }
        case TimePeriod.MONTH: {
          const startOfMonth = getStartOfCurrentMonth();
          const endOfMonth = getEndOfCurrentMonth();
          result = taskDateOnly >= startOfMonth && taskDateOnly <= endOfMonth;
          break;
        }
        case TimePeriod.YEAR: {
          const firstDayOfYear = new Date(todayOnly.getFullYear(), 0, 1);
          result = taskDateOnly >= firstDayOfYear && taskDateOnly <= todayOnly;
          break;
        }
        case TimePeriod.CUSTOM: {
          if (!dateRange.value.start || !dateRange.value.end) {
            result = true;
          } else {
            const startDate = new Date(dateRange.value.start);
            const endDate = new Date(dateRange.value.end);
            endDate.setHours(23, 59, 59, 999);
            startDate.setHours(0, 0, 0, 0);
            result = taskDateOnly >= startDate && taskDateOnly <= endDate;
          }
          break;
        }
        default:
          result = true;
      }

      console.log("Filter period:", selectedPeriod.value, "Result:", result);
      return result;
    }
  }

  function createFilteredTasks(tasks: any[]) {
    console.log("createFilteredTasks called with", tasks.length, "tasks");
    let filtered = tasks;

    // Apply topic filter
    if (selectedTopics.value.length > 0) {
      filtered = filtered.filter((task) =>
        selectedTopics.value.includes(task.topicId)
      );
      console.log("After topic filter:", filtered.length, "tasks");
    }

    // Apply project filter
    if (selectedProjects.value.length > 0) {
      filtered = filtered.filter((task) => {
        // Include tasks with no project if filtering by projects
        // Only filter if task has a projectId
        if (!task.projectId) {
          return false; // Exclude tasks without projects when filtering by projects
        }
        return selectedProjects.value.includes(task.projectId);
      });
      console.log("After project filter:", filtered.length, "tasks");
    }

    // Apply time period filter
    filtered = filtered.filter(isTaskInPeriod);
    console.log("After time period filter:", filtered.length, "tasks");

    // If filtering by sessions, also filter out sessions that don't fall within the period
    if (filterBySessions.value) {
      filtered = filtered.map((task) => {
        if (!task.sessions || task.sessions.length === 0) {
          return task;
        }

        const filteredSessions = task.sessions.filter((session: any) => {
          const sessionDate = new Date(session.startTime);
          const sessionDateOnly = new Date(
            sessionDate.getFullYear(),
            sessionDate.getMonth(),
            sessionDate.getDate()
          );
          const today = new Date();
          const todayOnly = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate()
          );

          switch (selectedPeriod.value) {
            case TimePeriod.ALL:
              return true;
            case TimePeriod.TODAY:
              return sessionDateOnly.getTime() === todayOnly.getTime();
            case TimePeriod.WEEK: {
              const startOfWeek = getStartOfCurrentWeek();
              const endOfWeek = getEndOfCurrentWeek();
              return (
                sessionDateOnly >= startOfWeek && sessionDateOnly <= endOfWeek
              );
            }
            case TimePeriod.MONTH: {
              const startOfMonth = getStartOfCurrentMonth();
              const endOfMonth = getEndOfCurrentMonth();
              return (
                sessionDateOnly >= startOfMonth && sessionDateOnly <= endOfMonth
              );
            }
            case TimePeriod.YEAR: {
              const firstDayOfYear = new Date(todayOnly.getFullYear(), 0, 1);
              return (
                sessionDateOnly >= firstDayOfYear &&
                sessionDateOnly <= todayOnly
              );
            }
            case TimePeriod.CUSTOM: {
              if (!dateRange.value.start || !dateRange.value.end) {
                return true;
              } else {
                const startDate = new Date(dateRange.value.start);
                const endDate = new Date(dateRange.value.end);
                endDate.setHours(23, 59, 59, 999);
                startDate.setHours(0, 0, 0, 0);
                return (
                  sessionDateOnly >= startDate && sessionDateOnly <= endDate
                );
              }
            }
            default:
              return true;
          }
        });

        // Recalculate task duration based on filtered sessions
        const filteredDuration = filteredSessions.reduce(
          (total: number, session: any) => {
            if (!session.endTime) return total;
            const sessionDuration =
              new Date(session.endTime).getTime() -
              new Date(session.startTime).getTime();
            return total + sessionDuration;
          },
          0
        );

        return {
          ...task,
          sessions: filteredSessions,
          duration: filteredDuration,
        };
      });
    }

    // Apply completion status filter
    console.log(
      "Completion status filter value:",
      selectedCompletionStatus.value
    );
    if (selectedCompletionStatus.value === CompletionStatus.COMPLETED) {
      filtered = filtered.filter((task) => task.completed);
      console.log("Filtering for completed tasks only");
    } else if (
      selectedCompletionStatus.value === CompletionStatus.NOT_COMPLETED
    ) {
      filtered = filtered.filter((task) => !task.completed);
      console.log("Filtering for not completed tasks only");
    } else if (selectedCompletionStatus.value === CompletionStatus.ALL) {
      // Show all tasks regardless of completion status
      console.log("Showing all tasks (no completion filter)");
    } else {
      // Fallback to showing all tasks for any other value
      console.log("Showing all tasks (fallback)");
    }
    console.log("After completion status filter:", filtered.length, "tasks");

    // Sort tasks by creation date (descending) and then by ID for stability
    filtered.sort((a, b) => {
      // Primary sort: creation date (descending - newest first)
      const aCreatedAt = new Date(a.createdAt || 0).getTime();
      const bCreatedAt = new Date(b.createdAt || 0).getTime();

      if (aCreatedAt !== bCreatedAt) {
        return bCreatedAt - aCreatedAt; // Descending order (newest first)
      }

      // Secondary sort: task ID (ascending) for stability
      return a.id - b.id;
    });

    console.log("Final filtered tasks:", filtered.length, "tasks");
    console.log(
      "Final task order:",
      filtered.map((t) => ({ id: t.id, name: t.name, isActive: t.isActive }))
    );
    return filtered;
  }

  return {
    selectedTopics,
    selectedProjects,
    selectedPeriod,
    dateRange,
    selectedCompletionStatus,
    filterBySessions,
    isTaskInPeriod,
    createFilteredTasks,
  };
}
